/**
 * PC-v2.2-C2.3-C — «REGISTRO DE INCIDENCIAS» (E4) Y «REGISTRO DE NO CONFORMIDADES» (E5) EN EXCEL
 *
 * Un bloque plegado sobre el listado (Calidad del portal y Calidad interna) que descarga el registro de las
 * incidencias o no conformidades REGISTRADAS en un periodo, con su historial. Qué columnas salen y sobre qué registros
 * lo decide el servidor (perspectiva, permisos, visibilidad para el cliente y ámbito); el navegador sólo dice el
 * periodo y repite los filtros de la vista.
 *
 *   · Filtros de la vista: el centro (o todos), el estado y el nivel del listado, tal cual. Los demás (responsable,
 *     «mías», vencidas, ubicación, vínculo con NC, drill-down de Indicadores) no se aplican al Excel: se nombran al lado
 *     (C2.3-C-R2), uno por uno, para que ninguno parezca aplicado sin estarlo.
 *   · Periodo: el de la vista si lo tiene (drill-down «registradas del … al …»), si no los últimos 90 días; se puede
 *     cambiar hasta 366 días (ambos incluidos) sin tocar el listado.
 *   · Gestión interna: el cliente es el del centro elegido; sin centro, se elige entre los clientes que alcanza la
 *     sesión (uno solo: ése). El servidor vuelve a comprobarlo todo (404 / 403).
 *   · Sólo se pinta si la sesión tiene los dos permisos del informe (el servidor los vuelve a exigir: 403).
 */
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { api } from '../api.ts';
import { useDatos } from './base.tsx';
import { useSesion } from '../contexto.tsx';
import { BotonExportarExcel } from './exportar-excel.tsx';

export const MAX_DIAS_CALIDAD = 366;
export const DIAS_POR_DEFECTO_CALIDAD = 90;

export type InformeCalidad = 'INCIDENCIAS' | 'NO_CONFORMIDADES';

const RUTA: Record<InformeCalidad, Record<'PORTAL' | 'INTERNA', string>> = {
  INCIDENCIAS: { PORTAL: '/api/portal/informes/incidencias/exportar', INTERNA: '/api/informes/incidencias/exportar' },
  NO_CONFORMIDADES: { PORTAL: '/api/portal/informes/no-conformidades/exportar', INTERNA: '/api/informes/no-conformidades/exportar' },
};
const TITULO: Record<InformeCalidad, string> = { INCIDENCIAS: 'Registro de incidencias', NO_CONFORMIDADES: 'Registro de no conformidades' };
const SUBTITULO: Record<InformeCalidad, string> = {
  INCIDENCIAS: 'Excel con las incidencias registradas en el periodo, su resolución y su historial de estados',
  NO_CONFORMIDADES: 'Excel con las no conformidades registradas en el periodo, sus acciones correctivas, su historial y sus comentarios',
};
const LEER: Record<InformeCalidad, string> = { INCIDENCIAS: 'incidencias:leer', NO_CONFORMIDADES: 'nc:leer' };

const RE_FECHA = /^\d{4}-\d{2}-\d{2}$/;
function dias(desde: string, hasta: string): number {
  return Math.round((Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000) + 1;
}
const isoLocal = (d: Date): string =>
  `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Los últimos 90 días (hoy incluido), en la fecha local del navegador. */
export function periodoPorDefectoCalidad(hoy: Date = new Date()): { desde: string; hasta: string } {
  const desde = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (DIAS_POR_DEFECTO_CALIDAD - 1));
  return { desde: isoLocal(desde), hasta: isoLocal(hoy) };
}

/** Por qué no se puede pedir ese periodo, o null si se puede. */
export function motivoPeriodoCalidad(desde: string, hasta: string): string | null {
  if (!RE_FECHA.test(desde) || !RE_FECHA.test(hasta) || Number.isNaN(Date.parse(desde)) || Number.isNaN(Date.parse(hasta))) return 'Indica las dos fechas del periodo.';
  if (hasta < desde) return '«Desde» no puede ser posterior a «Hasta».';
  if (dias(desde, hasta) > MAX_DIAS_CALIDAD) return `El registro admite como mucho ${String(MAX_DIAS_CALIDAD)} días.`;
  return null;
}

/** ¿Puede esta sesión exportar el registro en esa perspectiva? (las dos llaves de C2.3-A) */
export function puedeExportarCalidad(puede: (p: string) => boolean, tipo: InformeCalidad, perspectiva: 'PORTAL' | 'INTERNA'): boolean {
  return puede(perspectiva === 'PORTAL' ? 'informes_operativos:exportar_propios' : 'informes_operativos:exportar') && puede(LEER[tipo]);
}

export function ExportarInformeCalidad(props: {
  tipo: InformeCalidad;
  perspectiva: 'PORTAL' | 'INTERNA';
  /** El centro de la vista ('' = todos). */
  idCentro: string;
  /** El estado y el nivel del listado ('' o 'TODAS' = sin filtro). */
  estado: string;
  gravedad: string;
  /** El periodo de la vista, si lo tiene (drill-down). */
  desdeVista?: string;
  hastaVista?: string;
  /** Los filtros activos de la vista que el Excel NO aplica, por su nombre en pantalla. Se nombran. */
  noAplicados?: readonly string[];
}): ReactNode {
  const { puede } = useSesion();
  if (!puedeExportarCalidad(puede, props.tipo, props.perspectiva)) return null;
  return <BloqueExportarCalidad {...props} />;
}

interface CentroCalidad { id: string; descripcion: string; cliente: string; idCliente: string }

function BloqueExportarCalidad({ tipo, perspectiva, idCentro, estado, gravedad, desdeVista = '', hastaVista = '', noAplicados = [] }: Parameters<typeof ExportarInformeCalidad>[0]): ReactNode {
  const id = useId();
  const inicial = (): { desde: string; hasta: string } =>
    (RE_FECHA.test(desdeVista) && RE_FECHA.test(hastaVista) ? { desde: desdeVista, hasta: hastaVista } : periodoPorDefectoCalidad());
  const [desde, setDesde] = useState(() => inicial().desde);
  const [hasta, setHasta] = useState(() => inicial().hasta);
  useEffect(() => { const p = inicial(); setDesde(p.desde); setHasta(p.hasta); }, [desdeVista, hastaVista]); // eslint-disable-line react-hooks/exhaustive-deps

  //  Gestión interna: el cliente sale del centro (o se elige). El portal no lo manda: es el de la sesión.
  const interna = perspectiva === 'INTERNA';
  const centros = useDatos(
    () => (interna ? api.get<{ filas: CentroCalidad[] }>('/api/calidad/centros') : Promise.resolve({ filas: [] as CentroCalidad[] })),
    [interna],
  );
  const clientes = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of centros.datos?.filas ?? []) if (!m.has(c.idCliente)) m.set(c.idCliente, c.cliente);
    return [...m].map(([v, nombre]) => ({ id: v, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [centros.datos]);
  const [elegido, setElegido] = useState('');
  const delCentro = idCentro === '' ? '' : (centros.datos?.filas.find((c) => c.id === idCentro)?.idCliente ?? '');
  const idCliente = !interna ? '' : idCentro !== '' ? delCentro : clientes.length === 1 ? clientes[0]!.id : elegido;

  const motivo = motivoPeriodoCalidad(desde, hasta)
    ?? (interna && idCliente === '' ? (centros.datos === null ? 'Cargando los clientes…' : 'Elige el cliente.') : null);
  const parametros = new URLSearchParams({
    ...(interna ? { idCliente } : {}),
    desde, hasta,
    ...(idCentro === '' ? {} : { idCentro }),
    ...(estado === '' || estado === 'TODAS' ? {} : { estado }),
    ...(gravedad === '' ? {} : { gravedad }),
  });
  const ruta = interna && idCliente === '' ? null : `${RUTA[tipo][perspectiva]}?${parametros.toString()}`;

  return (
    <details className="panel registro-limpiezas">
      <summary className="panel-cuerpo">
        <strong>{TITULO[tipo]}</strong>
        <span className="pequena tenue"> · {SUBTITULO[tipo]}</span>
      </summary>
      <div className="panel-cuerpo fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="campo">
          <label htmlFor={`${id}-desde`}>Registradas desde</label>
          <input id={`${id}-desde`} type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor={`${id}-hasta`}>Hasta</label>
          <input id={`${id}-hasta`} type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        {interna && idCentro === '' && clientes.length > 1 && (
          <div className="campo">
            <label htmlFor={`${id}-cliente`}>Cliente</label>
            <select id={`${id}-cliente`} value={elegido} onChange={(e) => setElegido(e.target.value)}>
              <option value="">Elige…</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
        )}
        <BotonExportarExcel
          ruta={ruta} motivoDeshabilitado={motivo}
          etiqueta="Exportar registro"
          ayuda={`${TITULO[tipo]} del periodo elegido (hasta ${String(MAX_DIAS_CALIDAD)} días)`}
        />
      </div>
      <p className="panel-cuerpo pequena tenue" style={{ marginTop: 0 }}>
        Con el centro, el estado y el nivel del listado.
        {noAplicados.length > 0 && <> No se aplican al Excel: {noAplicados.join(', ')}.</>}
      </p>
      {motivo !== null && motivo !== 'Cargando los clientes…' && <p className="panel-cuerpo pequena" role="alert" style={{ marginTop: 0 }}>{motivo}</p>}
    </details>
  );
}
